import { useRef, useEffect, useState } from "react";

const LineChart = ({ data = [], title, subtitle, height = 300 }) => {
  const svgRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(0);

  const padding = 40;

  useEffect(() => {
    const updateWidth = () => {
      if (svgRef.current) {
        setChartWidth(svgRef.current.clientWidth || 0);
      }
    };

    updateWidth();

    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);
  // Extract unique x-axis labels across all series
  const allLabels = data.flatMap(
    (series) => series.data?.map((d) => d.label) || [],
  );
  const xLabels = [
    ...new Set(
      allLabels.length
        ? allLabels
        : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    ),
  ];

  const chartInnerWidth = Math.max(chartWidth - 2 * padding, 0);

  const xStep =
    chartInnerWidth > 0 && xLabels.length > 1
      ? chartInnerWidth / (xLabels.length - 1)
      : 0;

  // Get max value across all data and round up to nearest 5
  const maxValue = Math.max(
    1,
    ...data.flatMap((series) => series.data?.map((d) => d.value) || []),
  );

  const yMax = Math.max(5, Math.ceil(maxValue / 5) * 5);

  const chartHeight = height - 100;

  const getX = (label) => {
    const index = xLabels.indexOf(label);
    if (index === -1) return padding;
    return padding + index * xStep;
  };

  const getY = (value) => chartHeight + padding - (value / yMax) * chartHeight;

  // Create a smooth curved path between points using cubic Bezier segments
  const createPath = (seriesData) => {
    if (!seriesData || seriesData.length === 0) return "";

    const points = seriesData.map((point) => ({
      x: getX(point.label),
      y: getY(point.value),
    }));

    if (points.length === 1) {
      const { x, y } = points[0];
      return `M ${x} ${y}`;
    }

    let d = `M ${points[0].x} ${points[0].y}`;

    for (let i = 0; i < points.length - 1; i++) {
      const current = points[i];
      const next = points[i + 1];
      const controlX = current.x + (next.x - current.x) / 2;

      d += ` C ${controlX} ${current.y}, ${controlX} ${next.y}, ${next.x} ${next.y}`;
    }

    return d;
  };

  // Dark navy + teal to match design; third is fallback
  const colors = ["#1d4ed8", "#06b6d4", "#64748b"];

  return (
    <div className="chart-container w-100">
      <h3 className="chart-title">{title}</h3>
      <h6 className="chart-subtitle">{subtitle}</h6>

      <div className="line-chart">
        <svg
          ref={svgRef}
          width="100%"
          height={height}
          viewBox={`0 0 ${chartWidth} ${height}`}
        >
          {/* Horizontal grid lines */}
          {[0, 1, 2, 3, 4].map((i) => {
            const y = padding + i * (chartHeight / 4);
            return (
              <line
                key={`h-${i}`}
                x1={padding}
                y1={y}
                x2={chartWidth - padding}
                y2={y}
                stroke="#e2e8f0"
                strokeWidth="1"
                strokeDasharray="4 4"
              />
            );
          })}

          {/* Vertical grid lines (aligned with x-axis labels) */}
          {xLabels.map((label, index) => {
            const x = getX(label);
            return (
              <line
                key={`v-${index}`}
                x1={x}
                y1={padding}
                x2={x}
                y2={padding + chartHeight}
                stroke="#e2e8f0"
                strokeWidth="1"
                strokeDasharray="4 4"
              />
            );
          })}

          {/* X and Y axis borders */}
          <line
            x1={padding}
            y1={padding}
            x2={padding}
            y2={padding + chartHeight}
            stroke="#666666"
            strokeWidth="1"
          />
          <line
            x1={padding}
            y1={padding + chartHeight}
            x2={chartWidth - padding}
            y2={padding + chartHeight}
            stroke="#666666"
            strokeWidth="1"
          />

          {/* Y-axis labels */}
          {[0, 1, 2, 3, 4].map((i) => (
            <text
              key={i}
              x={padding - 10}
              y={padding + i * (chartHeight / 4) + 5}
              textAnchor="end"
              fontSize="12"
              fill="#64748b"
            >
              {Math.round(yMax - (i * yMax) / 4)}
            </text>
          ))}

          {/* Data lines and points */}
          {chartWidth > 0 &&
            data.map((series, seriesIndex) => (
              <g key={seriesIndex}>
                <path
                  d={createPath(series.data)}
                  fill="none"
                  stroke={colors[seriesIndex % colors.length]}
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                {series.data?.map((point, pointIndex) => (
                  <circle
                    key={pointIndex}
                    cx={getX(point.label)}
                    cy={getY(point.value)}
                    r="4"
                    fill="#ffffff"
                    stroke={colors[seriesIndex % colors.length]}
                    strokeWidth="2"
                  />
                ))}
              </g>
            ))}

          {/* X-axis labels */}
          {xLabels.map((label, index) => (
            <text
              key={index}
              x={padding + index * xStep}
              y={height - 20}
              textAnchor="middle"
              fontSize="12"
              fill="#64748b"
            >
              {label}
            </text>
          ))}
        </svg>

        {/* Legend */}
        {data.length > 0 && (
          <div className="d-flex justify-content-center gap-4 mt-3">
            {data.map((series, index) => (
              <div key={index} className="d-flex align-items-center gap-2">
                <div
                  className="rounded-circle"
                  style={{
                    width: "10px",
                    height: "10px",
                    backgroundColor: colors[index % colors.length],
                    borderRadius: "999px",
                  }}
                ></div>
                <span className="small">{series.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LineChart;
